import { getIdToken } from './auth';

/** Origin for REST `/api/v1/billing/...` calls (matches embedded checkout). */
export function getBillingApiOrigin(): string {
  const configuredApiUrl = process.env.REACT_APP_API_URL;
  if (configuredApiUrl) {
    try {
      const apiUrl = new URL(configuredApiUrl);
      return `${apiUrl.protocol}//${apiUrl.host}`;
    } catch {
      // fall through to hostname-based derivation
    }
  }

  if (typeof window !== 'undefined') {
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length >= 2) {
      const domain = hostParts.slice(-2).join('.');
      return `${window.location.protocol}//api.${domain}`;
    }
    return window.location.origin;
  }

  return '';
}

export type BillingOrderStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export type BillingOrderLine = {
  internal_sku: string;
  display_name: string;
  charge_type: string;
  quantity: number;
  unit_amount_cents: number;
  stripe_price_id: string | null;
};

export type BillingOrder = {
  id: number;
  status: string;
  currency: string;
  total_amount_cents: number;
  stripe_payment_intent_id: string | null;
  stripe_receipt_url: string | null;
  fulfilled_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  lines: BillingOrderLine[];
};

/** Laravel paginator slice returned by GET /api/v1/billing/orders */
export type BillingOrdersListResponse = {
  current_page: number;
  data: BillingOrder[];
  first_page_url: string | null;
  from: number | null;
  last_page: number;
  last_page_url: string | null;
  links?: {
    url: string | null;
    label: string;
    active: boolean;
  }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
};

export type FetchBillingOrdersParams = {
  page?: number;
  perPage?: number;
  status?: BillingOrderStatus;
};

export function formatBillingMoney(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export async function fetchBillingOrders(
  params: FetchBillingOrdersParams = {}
): Promise<BillingOrdersListResponse> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const search = new URLSearchParams();
  if (typeof params.page === 'number') {
    search.set('page', String(params.page));
  }
  if (typeof params.perPage === 'number') {
    search.set('per_page', String(params.perPage));
  }
  if (params.status) {
    search.set('status', params.status);
  }
  const qs = search.toString();
  const url = `${apiOrigin}/api/v1/billing/orders${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errorMessage = body?.message || body?.error || '';
    const fallback =
      response.status === 401
        ? 'Sign in required.'
        : response.status === 422
          ? 'Invalid request.'
          : `Orders request failed (${response.status})`;
    const error = new Error(errorMessage || fallback);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<BillingOrdersListResponse>;
}
